import { IconFont } from '@/components';
import { AlertPrimary } from '@/components/AlertPrimary';
import { CheckboxOptions } from '@/components/CheckboxOptions';
import { RoundedTab } from '@/components/RoundedTab';
import Tabs from '@/components/Tabs';
import { useBaseInfoStore } from '@/pages/clusters/pages/Workload/create/base-info';
import { WorkloadParams } from '@/pages/clusters/pages/Workload/types';
import { Button, Checkbox, Form, Select } from '@arco-design/web-react';
import { IconCheck, IconClose } from '@arco-design/web-react/icon';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useConfigKeyMaps, useSecretKeymaps } from '../hooks';
import { GrayBox } from '../tools';
import { EnvItem } from './_hooks';

const TabPane = Tabs.TabPane;
const FormItem = Form.Item;
const useCheckbox = Checkbox.useCheckbox;

interface LabelValue {
  label: string;
  value: string;
}

type TabKeys = Exclude<EnvItem['type'], 'custom'>;
export interface ReferenceData {
  type: TabKeys;
  /** 选择的资源 */
  selectedMap: string;
  /** 选择的资源中的键 */
  selectedKeys: string[];
}

interface ReferenceSelectProps {
  onCancel?: () => void;
  onOk?: (data: ReferenceData) => void;
}

export function ReferenceSelect({ onCancel, onOk }: ReferenceSelectProps) {
  const { t } = useTranslation();
  const msg = {
    desc: t('components:reference.multiple.keys.from.configmap.or.secret'),
    config_keymap: t('common:configmap'),
    secret_keymap: t('common:secret'),
    key: t('common:key'),
    select_all: t('components:select.all'),
    unselect_all: t('components:deselect.all'),
    placeholder: t('components:please.select'),
  };

  const { cluster } = useParams<WorkloadParams>();

  const [activeKey, setActiveKey] = React.useState<TabKeys>('configMapKeyRef');
  const { namespace } = useBaseInfoStore((state) => state.baseInfo);
  // 字典的待选项
  const { options: configsOptions, isLoading: isLoadingConfigs } = useConfigKeyMaps(
    cluster,
    namespace,
  );
  const { options: secretsOptions, isLoading: isLoadingSecrets } = useSecretKeymaps(
    cluster,
    namespace,
  );
  // 选择的配置字典或保密字典的值
  const [selectedConfig, setSelectedConfig] = React.useState();
  const [selectedSecret, setSelectedSecret] = React.useState();
  // 字典对应的键的待选项
  const [configKeysOptons, setConfigKeysOptons] = React.useState<LabelValue[]>([]);
  const [secretKeysOptons, setSecretKeysOptons] = React.useState<LabelValue[]>([]);

  const {
    selected: configKeysSelected,
    selectAll: selectAllConfigKeys,
    setSelected: setConfigKeysSelected,
    unSelectAll: unSelectAllConfigKey,
    isAllSelected: isAllConfigKeySelected,
  } = useCheckbox(configKeysOptons.map((x) => x.value));

  const {
    selected: secretKeysSelected,
    selectAll: selectAllSecretKeys,
    setSelected: setSecretKeysSelected,
    unSelectAll: unSelectAllSecretKey,
    isAllSelected: isAllSecretKeySelected,
  } = useCheckbox(secretKeysOptons.map((x) => x.value));

  // 取消
  const handleCancel = () => {
    onCancel?.();
  };

  // 保存
  const handleSave = () => {
    if (activeKey === 'configMapKeyRef') {
      onOk?.({
        type: activeKey,
        selectedMap: selectedConfig,
        selectedKeys: configKeysSelected,
      });
    }
    if (activeKey === 'secretKeyRef') {
      onOk?.({
        type: activeKey,
        selectedMap: selectedSecret,
        selectedKeys: secretKeysSelected,
      });
    }
  };

  return (
    <Box>
      <AlertPrimary content={msg.desc} />
      <RoundedTab activeTab={activeKey} onChange={(value: TabKeys) => setActiveKey(value)}>
        <TabPane title={msg.config_keymap} key="configMapKeyRef">
          <FormItem layout="vertical" label={msg.config_keymap}>
            <Select
              showSearch
              prefix={<IconFont name="icon-peizhizidian" />}
              placeholder={msg.placeholder}
              value={selectedConfig}
              onChange={(value) => {
                setSelectedConfig(value);
                // 查找当前选中的配置字典
                const selectedConfig = configsOptions.find((option) => option.value === value);
                // 更新键选项
                if (selectedConfig && selectedConfig.extraOptions) {
                  setConfigKeysOptons(selectedConfig.extraOptions);
                } else {
                  setConfigKeysOptons([]);
                }
              }}
              options={configsOptions}
              loading={isLoadingConfigs}
            />
          </FormItem>
          <FormLabel>
            <span>{msg.key}</span>
            <Button
              type="text"
              size="small"
              onClick={() => {
                !isAllConfigKeySelected() ? selectAllConfigKeys() : unSelectAllConfigKey();
              }}
              style={{ padding: 0 }}
            >
              {!isAllConfigKeySelected() ? msg.select_all : msg.unselect_all}
            </Button>
          </FormLabel>
          <FormItem layout="vertical">
            <KeyMapBox>
              <CheckboxOptions
                selected={configKeysSelected}
                onChange={setConfigKeysSelected}
                options={configKeysOptons}
              />
            </KeyMapBox>
          </FormItem>
        </TabPane>
        <TabPane title={msg.secret_keymap} key="secretKeyRef">
          <FormItem layout="vertical" label={msg.secret_keymap}>
            <Select
              showSearch
              prefix={<IconFont name="icon-baomizidian" />}
              placeholder={msg.placeholder}
              value={selectedSecret}
              onChange={(value) => {
                setSelectedSecret(value);
                // 查找当前选中的保密字典
                const selectedSecret = secretsOptions.find((option) => option.value === value);
                // 更新键选项
                if (selectedSecret && selectedSecret.extraOptions) {
                  setSecretKeysOptons(selectedSecret.extraOptions);
                } else {
                  setSecretKeysOptons([]);
                }
              }}
              options={secretsOptions}
              loading={isLoadingSecrets}
            />
          </FormItem>
          <FormLabel>
            <span>{msg.key}</span>
            <Button
              type="text"
              size="small"
              onClick={() => {
                !isAllSecretKeySelected() ? selectAllSecretKeys() : unSelectAllSecretKey();
              }}
              style={{ padding: 0 }}
            >
              {!isAllSecretKeySelected() ? msg.select_all : msg.unselect_all}
            </Button>
          </FormLabel>
          <FormItem layout="vertical">
            <KeyMapBox>
              <CheckboxOptions
                selected={secretKeysSelected}
                onChange={setSecretKeysSelected}
                options={secretKeysOptons}
              />
            </KeyMapBox>
          </FormItem>
        </TabPane>
      </RoundedTab>
      <FooterBox>
        <Button
          icon={<IconClose />}
          size="mini"
          type="secondary"
          style={{ width: 80 }}
          onClick={handleCancel}
        />
        <Button
          icon={<IconCheck />}
          size="mini"
          type="primary"
          style={{ width: 80 }}
          onClick={handleSave}
        />
      </FooterBox>
    </Box>
  );
}

/** ============================ 样式 ==================================== */
const Box = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormLabel = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const KeyMapBox = styled(GrayBox)`
  height: 160px;
  overflow-y: auto;
`;

const FooterBox = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;
